import React, { useState, useEffect, useRef, useCallback } from "react";
import axios from "axios";
import "./DriversMap.css";

const DriversMap = () => {
  const mapRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const markersRef = useRef([]);
  const infoWindowRef = useRef(null);
  const [drivers, setDrivers] = useState([]);
  const [selectedDriver, setSelectedDriver] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [mapStats, setMapStats] = useState({ total: 0, active: 0, ready: 0 });
  const [searchQuery, setSearchQuery] = useState("");
  const [filterStatus, setFilterStatus] = useState("all");

  const defaultCenter = { lat: 9.0205, lng: 38.8024 };

  const fetchDrivers = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axios.get("https://api.leambulance.com/activeDrivers/");
      const formattedDrivers = response.data.map((driver) => {
        const raw = (driver.status || '').toLowerCase();
        const status = raw === 'ready' ? 'Ready' : 'Busy';
        return ({
          id: driver.id,
          driverId: driver.driverId,
          queueNumber: driver.queueNumber,
          lat: parseFloat(driver.locationLatitude),
          lng: parseFloat(driver.locationLongitude),
          name: `${driver.driver2.firstName} ${driver.driver2.lastName}`,
          firstName: driver.driver2.firstName,
          lastName: driver.driver2.lastName,
          phoneNumber: driver.driver2.phoneNumber,
          status,
          vehicleType: driver.typeName || "Unknown Vehicle",
          vehicleTypeId: driver.vehicleTypeId,
          plateNumber: driver.vehicle?.plateNumber || "N/A",
          vehicleId: driver.vehicle?.id,
          ambulanceCompany: driver.driver2.ambulanceCompany,
          isActive: status === "Ready",
          lastUpdate: new Date().toISOString()
        });
      });
      
      setDrivers(formattedDrivers);
      
      // Calculate stats
      const stats = {
        total: formattedDrivers.length,
        ready: formattedDrivers.filter(d => d.status === "Ready").length,
        busy: formattedDrivers.filter(d => d.status === "Busy").length
      };
      setMapStats(stats);
      
    } catch (err) {
      console.error('Error fetching drivers:', err);
      setError("Failed to load drivers. Please check your connection and try again.");
    } finally {
      setLoading(false);
    }
  }, []);

  const getStatusColor = (status) => {
    const colors = {
      'Ready': '#10b981', // Emerald green
      'Busy': '#f59e0b', // Amber
    };
    return colors[status] || '#6b7280';
  };

  const createRippleEffect = (position, color = '#10b981') => {
    const ripple = new window.google.maps.Marker({
      position: position,
      map: mapInstanceRef.current,
      icon: {
        path: window.google.maps.SymbolPath.CIRCLE,
        scale: 8,
        fillColor: color,
        fillOpacity: 0.5,
        strokeColor: color,
        strokeWeight: 1,
        strokeOpacity: 0.8,
      },
      zIndex: 0
    });

    let scale = 8;
    let opacity = 0.5;

    const animate = () => {
      scale += 1.2;
      opacity -= 0.02;

      if (opacity <= 0 || scale > 40) {
        ripple.setMap(null);
        return;
      }

      ripple.setIcon({
        path: window.google.maps.SymbolPath.CIRCLE,
        scale: scale,
        fillColor: color,
        fillOpacity: opacity,
        strokeColor: color,
        strokeWeight: 1,
        strokeOpacity: opacity * 0.7,
      });

      requestAnimationFrame(animate);
    };

    animate();
  };

  const createAmbulanceMarker = (driver) => {
    const statusColor = getStatusColor(driver.status);
    const isReady = driver.status === 'Ready';
    
    // Simple circle marker that will work reliably
    const markerIcon = {
      path: window.google.maps.SymbolPath.CIRCLE,
      fillColor: statusColor,
      fillOpacity: 0.8,
      strokeColor: '#ffffff',
      strokeWeight: 3,
      scale: isReady ? 12 : 10,
    };
    
    // Add a cross symbol for ambulance
    const crossIcon = {
      path: 'M-6,-2 L-6,2 L-2,2 L-2,6 L2,6 L2,2 L6,2 L6,-2 L2,-2 L2,-6 L-2,-6 L-2,-2 Z',
      fillColor: '#ffffff',
      fillOpacity: 1,
      strokeColor: statusColor,
      strokeWeight: 1,
      scale: isReady ? 0.8 : 0.6,
      anchor: new window.google.maps.Point(0, 0),
    };

    const marker = new window.google.maps.Marker({
      position: { lat: driver.lat, lng: driver.lng },
      map: mapInstanceRef.current,
      icon: markerIcon,
      title: `${driver.name} - ${driver.status}`,
      zIndex: isReady ? 10 : 5,
      optimized: false
    });
    
    // Add a second marker for the cross symbol
    const crossMarker = new window.google.maps.Marker({
      position: { lat: driver.lat, lng: driver.lng },
      map: mapInstanceRef.current,
      icon: crossIcon,
      zIndex: isReady ? 11 : 6,
      optimized: false,
      clickable: false
    });

    // Add pulsing effect for ready drivers
    if (isReady) {
      const pulseInterval = setInterval(() => {
        createRippleEffect({ lat: driver.lat, lng: driver.lng }, statusColor);
      }, 4000);
      
      // Store interval reference for cleanup
      marker.pulseInterval = pulseInterval;
    }

    // Enhanced hover effects
    marker.addListener('mouseover', () => {
      marker.setIcon({
        ...markerIcon,
        scale: (isReady ? 12 : 10) * 1.3,
        fillOpacity: 1,
      });
      crossMarker.setIcon({
        ...crossIcon,
        scale: (isReady ? 0.8 : 0.6) * 1.3,
      });
    });

    marker.addListener('mouseout', () => {
      marker.setIcon(markerIcon);
      crossMarker.setIcon(crossIcon);
    });

    // Click handler with info window
    marker.addListener('click', () => {
      setSelectedDriver(driver);
      
      if (infoWindowRef.current) {
        infoWindowRef.current.close();
      }
      
      const infoContent = `
        <div class="custom-info-window">
          <div class="info-header">
            <h4>${driver.name}</h4>
            <span class="status-badge status-${driver.status.toLowerCase()}">${driver.status}</span>
          </div>
          <div class="info-body">
            <p><strong>Vehicle:</strong> ${driver.vehicleType}</p>
            <p><strong>Plate:</strong> ${driver.plateNumber}</p>
            <p><strong>Queue:</strong> #${driver.queueNumber}</p>
          </div>
        </div>
      `;
      
      infoWindowRef.current = new window.google.maps.InfoWindow({
        content: infoContent,
        position: { lat: driver.lat, lng: driver.lng },
      });
      
      infoWindowRef.current.open(mapInstanceRef.current);
    });

    // Store reference to both markers for cleanup
    marker.crossMarker = crossMarker;
    return marker;
  };

  const initializeMap = () => {
    if (!mapRef.current) return;

    const mapStyles = [
      {
        "elementType": "geometry",
        "stylers": [{ "color": "#f5f5f5" }]
      },
      {
        "elementType": "labels.icon",
        "stylers": [{ "visibility": "off" }]
      },
      {
        "elementType": "labels.text.fill",
        "stylers": [{ "color": "#616161" }]
      },
      {
        "elementType": "labels.text.stroke",
        "stylers": [{ "color": "#f5f5f5" }]
      },
      {
        "featureType": "administrative.land_parcel",
        "elementType": "labels.text.fill",
        "stylers": [{ "color": "#bdbdbd" }]
      },
      {
        "featureType": "poi",
        "elementType": "geometry",
        "stylers": [{ "color": "#eeeeee" }]
      },
      {
        "featureType": "poi",
        "elementType": "labels.text.fill",
        "stylers": [{ "color": "#757575" }]
      },
      {
        "featureType": "poi.park",
        "elementType": "geometry",
        "stylers": [{ "color": "#e5e5e5" }]
      },
      {
        "featureType": "poi.park",
        "elementType": "labels.text.fill",
        "stylers": [{ "color": "#9e9e9e" }]
      },
      {
        "featureType": "road",
        "elementType": "geometry",
        "stylers": [{ "color": "#ffffff" }]
      },
      {
        "featureType": "road.arterial",
        "elementType": "labels.text.fill",
        "stylers": [{ "color": "#757575" }]
      },
      {
        "featureType": "road.highway",
        "elementType": "geometry",
        "stylers": [{ "color": "#dadada" }]
      },
      {
        "featureType": "road.highway",
        "elementType": "labels.text.fill",
        "stylers": [{ "color": "#616161" }]
      },
      {
        "featureType": "road.local",
        "elementType": "labels.text.fill",
        "stylers": [{ "color": "#9e9e9e" }]
      },
      {
        "featureType": "transit.line",
        "elementType": "geometry",
        "stylers": [{ "color": "#e5e5e5" }]
      },
      {
        "featureType": "transit.station",
        "elementType": "geometry",
        "stylers": [{ "color": "#eeeeee" }]
      },
      {
        "featureType": "water",
        "elementType": "geometry",
        "stylers": [{ "color": "#c9c9c9" }]
      },
      {
        "featureType": "water",
        "elementType": "labels.text.fill",
        "stylers": [{ "color": "#9e9e9e" }]
      }
    ];

    mapInstanceRef.current = new window.google.maps.Map(mapRef.current, {
      center: defaultCenter,
      zoom: 13,
      styles: mapStyles,
      disableDefaultUI: true,
      zoomControl: true,
      mapTypeControl: false,
      scaleControl: true,
      streetViewControl: false,
      rotateControl: false,
      fullscreenControl: true,
      gestureHandling: 'cooperative'
    });

    const trafficLayer = new window.google.maps.TrafficLayer();
    trafficLayer.setMap(mapInstanceRef.current);
    renderMarkers();
  };

  const renderMarkers = () => {
    if (!mapInstanceRef.current) return;

    // Clear existing markers and their intervals
    markersRef.current.forEach((marker) => {
      if (marker.pulseInterval) {
        clearInterval(marker.pulseInterval);
      }
      if (marker.crossMarker) {
        marker.crossMarker.setMap(null);
      }
      marker.setMap(null);
    });
    markersRef.current = [];

    // Filter drivers based on search and status filter
    const filteredDrivers = drivers.filter(driver => {
      const matchesSearch = searchQuery === '' || 
        driver.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        driver.plateNumber.toLowerCase().includes(searchQuery.toLowerCase()) ||
        driver.vehicleType.toLowerCase().includes(searchQuery.toLowerCase());
      
      const statusLower = driver.status.toLowerCase();
      const matchesStatus = filterStatus === 'all' || 
        (filterStatus === 'ready' && statusLower === 'ready') ||
        (filterStatus === 'busy' && statusLower === 'busy');
      
      return matchesSearch && matchesStatus;
    });

    filteredDrivers.forEach((driver) => {
      const marker = createAmbulanceMarker(driver);
      markersRef.current.push(marker);
    });
    
    // Auto-fit bounds if we have drivers
    if (filteredDrivers.length > 0 && mapInstanceRef.current) {
      const bounds = new window.google.maps.LatLngBounds();
      filteredDrivers.forEach(driver => {
        bounds.extend(new window.google.maps.LatLng(driver.lat, driver.lng));
      });
      mapInstanceRef.current.fitBounds(bounds, { padding: 50 });
    }
  };

  const loadGoogleMapsScript = () => {
    if (window.google && window.google.maps) {
      initializeMap();
      return;
    }

    // Check if API key exists
    const apiKey = process.env.REACT_APP_GOOGLE_MAP_API_KEY;
    if (!apiKey) {
      setError("Google Maps API key is not configured. Please add REACT_APP_GOOGLE_MAP_API_KEY to your .env file.");
      return;
    }

    const script = document.createElement("script");
    script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&libraries=places,geometry`;
    script.async = true;
    script.onload = initializeMap;
    script.onerror = () => setError("Failed to load Google Maps. Please check your API key and internet connection.");
    document.head.appendChild(script);
  };

  const refreshDrivers = () => {
    fetchDrivers();
  };

  useEffect(() => {
    fetchDrivers();
    loadGoogleMapsScript();

    // Set up auto-refresh every 30 seconds
    const refreshInterval = setInterval(fetchDrivers, 30000);

    return () => {
      clearInterval(refreshInterval);
      markersRef.current.forEach((marker) => {
        if (marker.pulseInterval) {
          clearInterval(marker.pulseInterval);
        }
        if (marker.crossMarker) {
          marker.crossMarker.setMap(null);
        }
        marker.setMap(null);
      });
      markersRef.current = [];
      if (infoWindowRef.current) {
        infoWindowRef.current.close();
      }
    };
  }, [fetchDrivers]);

  useEffect(() => {
    renderMarkers();
  }, [drivers, searchQuery, filterStatus]);

  return (
    <div className="drivers-map-wrapper">
      {/* Header Controls */}
      <div className="map-controls">
        <div className="controls-left">
          <div className="search-container">
            <input
              type="text"
              placeholder="Search drivers, vehicles, or plate numbers..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="search-input"
            />
            <span className="search-icon">🔍</span>
          </div>
          <select 
            value={filterStatus} 
            onChange={(e) => setFilterStatus(e.target.value)}
            className="status-filter"
          >
            <option value="all">All Status</option>
            <option value="ready">Ready</option>
            <option value="busy">Busy</option>
          </select>
        </div>
        
        <div className="controls-right">
          <button 
            onClick={refreshDrivers} 
            className="refresh-btn"
            disabled={loading}
          >
            🔄 Refresh
          </button>
        </div>
      </div>

      {/* Stats Dashboard */}
      <div className="stats-dashboard">
        <div className="stat-item">
          <span className="stat-number">{mapStats.total}</span>
          <span className="stat-label">Total Drivers</span>
        </div>
        <div className="stat-item ready">
          <span className="stat-number">{mapStats.ready}</span>
          <span className="stat-label">Ready</span>
        </div>
        <div className="stat-item busy">
          <span className="stat-number">{mapStats.busy}</span>
          <span className="stat-label">Busy</span>
        </div>
      </div>

      {/* Loading Overlay */}
      {loading && (
        <div className="loading-overlay">
          <div className="loading-spinner">
            <div className="spinner"></div>
            <span>Loading ambulance drivers...</span>
          </div>
        </div>
      )}
      
      {/* Error Message */}
      {error && (
        <div className="error-message">
          <span className="error-icon">⚠️</span>
          {error}
          <button onClick={refreshDrivers} className="retry-btn">Retry</button>
        </div>
      )}

      {/* Map Container */}
      <div ref={mapRef} className="map-container" />

      {/* Enhanced Driver Info Card */}
      {selectedDriver && (
        <div className="driver-info-card">
          <div className="driver-info-header">
            <div className="driver-avatar">
              {selectedDriver.firstName.charAt(0)}{selectedDriver.lastName.charAt(0)}
            </div>
            <div className="driver-title">
              <h3>{selectedDriver.name}</h3>
              <span className={`status-badge status-${selectedDriver.status.toLowerCase()}`}>
                {selectedDriver.status}
              </span>
            </div>
            <button
              onClick={() => setSelectedDriver(null)}
              className="close-button"
            >
              ✕
            </button>
          </div>
          
          <div className="driver-info-content">
            <div className="info-section">
              <h4 className="section-title">🚑 Vehicle Information</h4>
              <div className="info-row">
                <span className="info-label">Type</span>
                <span className="info-value">{selectedDriver.vehicleType}</span>
              </div>
              <div className="info-row">
                <span className="info-label">Plate Number</span>
                <span className="info-value plate-number">{selectedDriver.plateNumber}</span>
              </div>
              <div className="info-row">
                <span className="info-label">Queue Position</span>
                <span className="info-value">#{selectedDriver.queueNumber}</span>
              </div>
            </div>
            
            <div className="info-section">
              <h4 className="section-title">📱 Contact Information</h4>
              <div className="info-row">
                <span className="info-label">Phone</span>
                <a href={`tel:${selectedDriver.phoneNumber}`} className="info-value phone-link">
                  {selectedDriver.phoneNumber}
                </a>
              </div>
              <div className="info-row">
                <span className="info-label">Driver ID</span>
                <span className="info-value">{selectedDriver.driverId}</span>
              </div>
            </div>
            
            <div className="info-section">
              <h4 className="section-title">📍 Location Details</h4>
              <div className="info-row">
                <span className="info-label">Coordinates</span>
                <span className="info-value coordinates">
                  {selectedDriver.lat.toFixed(6)}, {selectedDriver.lng.toFixed(6)}
                </span>
              </div>
              <div className="info-row">
                <span className="info-label">Last Update</span>
                <span className="info-value">
                  {new Date(selectedDriver.lastUpdate).toLocaleTimeString()}
                </span>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DriversMap;